/*!
 * devextreme-react
 * Version: 24.2.5
 * Build date: Fri Feb 14 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-react
 */

import { Children, useContext, useRef, useLayoutEffect, } from 'react';
import { ElementType, getElementType } from './configuration/react/element';
import { mergeNameParts } from './configuration/utils';
import { createConfigBuilder } from './configuration/config-node';
import { getNamedTemplate } from './configuration/react/templates';
import { NestedOptionContext } from './contexts';
export function useOptionScanning(optionElement, children, templateContainer, parentUpdateToken, parentType) {
    const parentContext = useContext(NestedOptionContext);
    const { parentFullName, } = parentContext;
    const updateToken = Symbol('update token');
    const configBuilder = createConfigBuilder(optionElement, parentFullName);
    Children.forEach(children, (child) => {
        const elementType = getElementType(child);
        if (elementType === ElementType.Template) {
            const templateElement = child;
            const template = getNamedTemplate(templateElement.props);
            if (template) {
                configBuilder.addTemplate(template);
            }
        }
    });
    const childComponentCounter = useRef(0);
    const context = {
        parentExpectedChildren: optionElement.descriptor.expectedChildren,
        parentFullName: mergeNameParts(parentFullName, optionElement.descriptor.name),
        parentType,
        treeUpdateToken: updateToken,
        getOptionComponentKey: () => {
            childComponentCounter.current += 1;
            return childComponentCounter.current;
        },
        onChildOptionsReady: (childConfigNode, childDescriptor, childUpdateToken, childComponentKey) => {
            if (childUpdateToken !== updateToken) {
                return;
            }
            const { isCollection, name } = childDescriptor;
            if (isCollection) {
                configBuilder.addCollectionNode(name, childConfigNode, childComponentKey);
                return;
            }
            configBuilder.addChildNode(name, childConfigNode);
        },
    };
    useLayoutEffect(() => {
        const container = templateContainer && 'current' in templateContainer
            ? templateContainer.current
            : templateContainer;
        const hasTemplateRendered = !!container && container.childNodes.length > 0;
        configBuilder.updateAnonymousTemplates(hasTemplateRendered);
    }, [parentUpdateToken]);
    return [configBuilder.node, context];
}
